<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import type { SearchHistory } from '../../types/search'
import type { BibleVersion } from '../../types/bible'
import CascadeSelect from '../bible-reading/CascadeSelect.vue'

interface Props {
  selectedVersion?: string
  isSearchEnabled?: boolean
  isLoading?: boolean
  versions?: BibleVersion[]
  searchHistory?: SearchHistory[]
}

const props = withDefaults(defineProps<Props>(), {
  isSearchEnabled: true,
  isLoading: false,
  versions: () => [],
  searchHistory: () => [],
})

const emit = defineEmits<{
  'version-change': [versionCode: string]
  'search': [query: string, versionCode: string]
  'history-select': [query: string]
  'clear-history': []
}>()

const searchQuery = ref('')
const isHistoryOpen = ref(false)
const versionSelectOpen = ref(false)

const versionOptions = computed(() => {
  return props.versions.map(v => ({
    value: v.code,
    label: v.name,
  }))
})

const filteredHistory = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.searchHistory.slice(0, 20)
  }

  const query = searchQuery.value.toLowerCase()
  return props.searchHistory
    .filter(h => h.query.toLowerCase().includes(query))
    .slice(0, 20)
})

const handleVersionChange = (versionCode: string | number): void => {
  emit('version-change', String(versionCode))
  versionSelectOpen.value = false
  isHistoryOpen.value = false
  searchQuery.value = ''
}

const handleSearch = (): void => {
  const query = searchQuery.value.trim()
  if (!query || !props.selectedVersion) {
    return
  }
  emit('search', query, props.selectedVersion)
  isHistoryOpen.value = false
}

const handleHistorySelect = (query: string): void => {
  searchQuery.value = query
  emit('history-select', query)
  isHistoryOpen.value = false
  nextTick(() => {
    handleSearch()
  })
}

const handleKeydown = (e: KeyboardEvent): void => {
  if (e.key === 'Enter') {
    e.preventDefault()
    handleSearch()
  } else if (e.key === 'Escape') {
    isHistoryOpen.value = false
  }
}

const handleSearchInputFocus = (): void => {
  if (filteredHistory.length > 0) {
    isHistoryOpen.value = true
  }
}

const handleClearHistory = (): void => {
  emit('clear-history')
}

const getRelativeTime = (timestamp: number): string => {
  const now = Date.now()
  const diff = now - timestamp
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) return '少於 1 分鐘'
  if (minutes < 60) return `${minutes} 分鐘前`
  if (hours < 24) return `${hours} 小時前`
  if (days < 7) return `${days} 天前`

  const date = new Date(timestamp)
  return date.toLocaleDateString('zh-TW')
}
</script>

<template>
  <div class="keyword-search-toolbar">
    <div class="toolbar-row">
      <div style="width: 120px; flex-shrink: 0">
        <CascadeSelect
          :model-value="selectedVersion"
          :options="versionOptions"
          placeholder="選擇譯本"
          searchable
          :is-open="versionSelectOpen"
          @update:model-value="handleVersionChange"
          @update:is-open="(v) => { versionSelectOpen = v }"
        />
      </div>

      <div class="search-input-container">
        <div class="search-input-wrapper">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="輸入搜尋關鍵字..."
            :disabled="!selectedVersion"
            class="search-input"
            @keydown="handleKeydown"
            @focus="handleSearchInputFocus"
            @blur="() => setTimeout(() => { isHistoryOpen = false }, 200)"
          />

          <!-- 搜尋歷史下拉菜單 -->
          <div v-if="isHistoryOpen && filteredHistory.length > 0" class="history-dropdown">
            <div class="history-header">
              <span class="history-title">搜尋歷史 ({{ filteredHistory.length }})</span>
              <button
                class="clear-history-btn"
                @click.stop="handleClearHistory"
                title="清除此版本歷史"
              >
                ×
              </button>
            </div>

            <div class="history-list">
              <div
                v-for="item in filteredHistory"
                :key="item.id"
                class="history-item"
                @click.stop="handleHistorySelect(item.query)"
              >
                <div class="history-query">{{ item.query }}</div>
                <div class="history-meta">
                  <span class="result-count">({{ item.resultCount }} 筆)</span>
                  <span class="time">{{ getRelativeTime(item.timestamp) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          class="search-btn"
          :disabled="!selectedVersion || !searchQuery.trim()"
          @click="handleSearch"
        >
          <span v-if="isLoading" class="loading-spinner" />
          <span v-else>搜尋</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.keyword-search-toolbar {
  padding: 8px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 32px;
}

.search-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 200px;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  min-width: 150px;
}

.search-input {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  height: 24px;
  background-color: white;
  color: #1f2937;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.search-input:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.history-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-width: 400px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.history-title {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.clear-history-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}

.clear-history-btn:hover {
  color: #ef4444;
}

.history-list {
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
}

.history-item {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f3f4f6;
}

.history-item:last-child {
  border-bottom: none;
}

.history-item:hover {
  background-color: #f3f4f6;
}

.history-query {
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
  margin-bottom: 2px;
  word-break: break-all;
}

.history-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #9ca3af;
}

.result-count {
  color: #6b7280;
}

.search-btn {
  padding: 4px 12px;
  height: 24px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 60px;
}

.search-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.search-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.search-btn:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
